import { createSlice } from "@reduxjs/toolkit";
import { isEqual, merge } from "lodash";
import { updateWheelSegmentsNodes } from "../../utils/segmentBuilderUtils";
// Initial state with history tracking
const initialState = {
  wheel_name: "",
  take_screenshot: false,
  isSavingWheel: false,
  wheel_preview: "",
  type: 2, // custom wheel
  version: 2, // new editor
  selected_segment_char_count: 0, // New state for tracking character count
  business_box: {
    id: null,
    user: null,
    name: "",
    business_email: "",
    business_url: "",
    is_visible: null,
    is_base: null,
    business_image: "",
    advert_image: "",
    business_title: "",
    description: "",
    business_link: [],
  },
  automatic_resizing: false,
  content_heights: {},
  is_primary: null,
  // wheel-privacy-default-private: a new Wheel the user hasn't chosen a
  // visibility for yet must never default to Public (1) — Private (2) is the
  // privacy-safe unchosen default. The Create-Wheel wizard's visibility step
  // and the Access/Privacy tab both read this initial value before the user
  // makes an explicit choice.
  visibility: 2,
  is_locked: false,
  // bank-1003-overnight-conflict-and-red-sweeper: this used to be declared
  // TWICE in this object literal -- a second `lock_type` further down
  // (dead since 2024-11-27, shadowing this one) hardcoded is_active:true on
  // both sub-locks with a leftover debug description, so every brand-new
  // wheel's lock toggles (WheelLockingSettingsModal / LockSettingsModal via
  // getLockAccessCodePermission/getLockEmailPermission) rendered ON before
  // the user configured anything. setLockEmailPermission/setLockAccessCodes
  // mutate access_code_lock/permission_lock in place, so the sub-objects
  // must stay present (not `{}`) -- only is_active goes back to false.
  lock_type: {
    access_code_lock: {
      is_active: false,
      access_codes: [],
    },
    permission_lock: {
      is_active: false,
      members: [],
      emails: [],
    },
  },
  show_business_box: null,
  is_cloning: false,
  description: "",
  sub_title: "",
  category: null,
  // ISP-9999 (gordon-0904-wheel-categories-multi5-plus-custom-tags):
  // `categories` (up to 5 curated slugs) is now the source of truth the
  // builder's Info tab reads/writes; `category` above stays for backward
  // compat (server keeps it synced to categories[0]) but is no longer driven
  // by any FE control. `wheel_tags` mirrors the API shape (`[{name}]` or
  // `[{id, name}]` once hydrated from a GET) so `addWheelData` spreading a
  // server response needs no transform.
  categories: [],
  wheel_tags: [],
  background: {
    type: "",
    value: "",
  },
  image_links: [],
  video_links: [],
  document_links: [],
  audio_links: [],
  starred_links: [],
  wheel_image: "",
  wheel_center_media: {
    type: "image",
    url: "",
  },
  segment_icon: "blank",
  wheel_segments: [],
  designTabTarget: null,
  isSidePaneVisible: false,
  isToolBarVisible: false,
  isSegmentSettingVisible: false,
  is_rotate_box_open: false,
  froala_focus: null,

  is_clonable: true,
  // T22.153: per-wheel "Allow comments" (Wheel.comments_enabled, BE default
  // True). Hydrated from the GET by addWheelData and saved by the normal wheel
  // PUT, so flipping it marks the wheel changed like any other Access setting.
  comments_enabled: true,
  is_live: false,
  is_draft: false,
  // minors-13plus B.14: the creator's maturity self-label. "general" (not
  // "mature") is the default, so a wheel is never silently 18+. Declared here
  // so addWheelData's edited-field diff can see it as a first-class key when
  // the server hydrates one in.
  content_rating: "general",
  isGlobalSettingsApplied: false,
  isGlobalCornerRoundingApplied: false,
  isGlobalBorderThicknessApplied: false,
  isGlobalMarginApplied: false,
  isGlobalHeadingPropsApplied: false,
  isGlobalParagraphPropsApplied: false,
  isGlobalLineHeightApplied: false,
  globalSettings: {
    headingProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 20,
      resetKey: 0,
    },
    paragraphProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    },
    lineHeight: 1.15,
    cornerRounding: 8,
    borderThickness: 8,
    margin: {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20,
    },
  },
};

// Initial state with history tracking
const initialHistoryState = {
  history: [initialState], // This will store all past states
  currentIndex: 0, // Tracks the current state index in history
  isUndoRedoAction: false, // Flag to track undo/redo actions
  hasUnsavedChanges: false, // Track if there are unsaved changes
  lastSavedIndex: 0, // Track the history index of the last save
  // T22.109: bumped by `applyRemoteOp` each time a teammate's live co-edit op
  // is applied. pages/SegmentBuilder/index.jsx reads it to re-anchor its
  // content-dirty baseline in the SAME render, so a teammate's edit is never
  // mistaken for a local unsaved edit (which made every open peer auto-save an
  // echo PUT of it).
  remoteOpSeq: 0,
  // Bumped ONLY by undo/redo. The open segment's craft.js canvas re-hydrates off this so an
  // undo/redo repaints the visible editor (not just Redux/preview). Never bumped by live edits.
  undoRedoRevision: 0,
  // True while an undo/redo is still settling (raised by the undo/redo handlers, lowered a short
  // window later). While set, `withHistory` applies mutations but records NO history checkpoint, so
  // the reactive re-hydration echoes an undo/redo triggers (canvas re-serialize, D3 chart feedback,
  // segment-settings initializers) can't record a phantom snapshot and trim the redo-future
  // (WEB-038). See `withHistory` in redux/actions/history.js.
  historySuppressed: false,
  // fe-builder-first-edit-after-load-silently-dropped: set by `addWheelData`
  // whenever its pre-hydration-edit reconciliation (see that reducer) actually
  // replayed a real user edit on top of the freshly hydrated server payload.
  // The hydrate success handler (pages/SegmentBuilder/index.jsx) dispatches an
  // unconditional `markChangesSaved()` right after hydration so a freshly
  // loaded, UNedited wheel reads "All changes saved" instead of a false
  // "Unsaved changes" (WEB-E2E-3) -- but that same unconditional call also
  // re-anchors `lastSavedIndex` onto a REAL edit the user made while the fetch
  // was still in flight, marking it "saved" even though it was never sent in a
  // PUT. This flag lets that call site skip `markChangesSaved()` for exactly
  // that case, so the edit stays genuinely dirty and the normal autosave path
  // actually persists it.
  preHydrationEditPending: false,
  // ISP-9999 (undo-spam D3 arity crash): which right-side menu/pane is open.
  // Top-level ephemeral UI field, OUTSIDE undo/redo history — same treatment as
  // activeDesignSubTab/segmentResizeView/resizeSegmentMode below, and for the
  // same reason ("an undo must never flip which tool the user is looking at").
  // This used to live at history[currentIndex].selectedMenu, so spamming Undo
  // rapidly replayed whatever pane was open at each earlier history entry
  // (including flipping the Capture drawer in/out via selectedMenu === 8).
  // pages/SegmentBuilder/index.jsx's firstDivStyles react-spring width tween
  // formats its target differently when the Capture drawer is open
  // (`calc(94vw - ${CAPTURE_DRAWER_WIDTH_PX}px)`, 2 embedded numbers) than
  // otherwise (a plain `"94vw"`/`"68vw"`/`"64vw"`, 1 embedded number).
  // react-spring's string interpolator requires every queued target to have
  // the SAME arity of embedded numbers; undo-spamming faster than the pane's
  // 500ms spring settle queued both shapes at once and threw
  // 'Error: The arity of each "output" value must be equal'. Keeping
  // selectedMenu out of history removes the undo-driven churn entirely.
  selectedMenu: 1,
  // W-A0 (resize subpane gating): which DesignTab sub-tab is currently active
  // ("Theme"/"Portrait"/"Wallpaper"/"Style"/"Segments"), or null when the Design
  // pane isn't mounted. Pure ephemeral UI state kept OUTSIDE the undo/redo history
  // on purpose — an undo must never flip which tool the user is looking at. Read
  // by the flag-gated wedge drag-resize overlay so its grips mount ONLY while the
  // user is in the Resize ("Segments") subsection, not in every builder context.
  activeDesignSubTab: null,
  // V1-RESIZE (flag REACT_APP_RESIZE_WEDGE_DRAG): whether the per-Segment Settings
  // pane (selectedMenu === 2) is currently showing the PURE per-Segment resize
  // view (V1) — only THAT segment's Segment Size typed input + the drag-to-resize
  // toggle — instead of the full segment editor. Opened by clicking the "Segment
  // Size" entry in the segment editor; closed by the view's Back control. Ephemeral
  // UI state kept OUTSIDE undo/redo on purpose (an undo must not flip which view the
  // user is looking at) — same treatment as activeDesignSubTab. Flag OFF: never set,
  // and the "Segment Size" entry keeps its legacy jump to the Design > Segments pane,
  // so the builder is byte-identical to today.
  segmentResizeView: false,
  // W-A0 (resize-mode gating): whether the user has explicitly entered "Resize
  // Segment" mode via the side-pane toggle (reachable from BOTH the Wheel-tab
  // Design > Segments pane AND the per-Segment Settings pane). The flag-gated
  // wedge drag-resize overlay mounts its grips ONLY while this is true — the drag
  // is INERT otherwise, which is the root-cause fix for the 2026-07-14 rollback
  // (the drag was active whenever the flag was on, so it resized by accident).
  // Pure ephemeral UI state kept OUTSIDE undo/redo history (an undo must never
  // flip which tool the user is holding). Default false; reset when the builder
  // unmounts so the mode never persists across a fresh open.
  resizeSegmentMode: false,
};

/**
 * Helper function to get the present state based on the currentIndex
 */
export const getPresentState = (state) =>
  state.history[state.currentIndex] || {};

/**
 * Helper function to push the present state into the history and update the index.
 */
export const pushToHistory = (state) => {
  const newPresent = { ...getPresentState(state) }; // Copy the present state
  const previousPresent = state.history[state.currentIndex]; // Get the current state in history

  // Only push to history if the new present state is different from the previous one
  if (!isEqual(newPresent, previousPresent)) {
    const newHistory = state.history.slice(0, state.currentIndex + 1); // Trim any future history
    newHistory.push(newPresent); // Add the new present state
    state.history = newHistory;
    state.currentIndex = newHistory.length - 1; // Move currentIndex to the latest state
    console.log("History updated with new present state.");
  } else {
    console.log("No state change, history not updated.");
  }
};

const segmentBuilder = createSlice({
  name: "segmentBuilder",
  initialState: initialHistoryState,
  reducers: {
    // Action to push current state to history
    pushToHistoryAction: (state) => {
      if (state.isUndoRedoAction) return; // Don't push to history during undo/redo
      console.log("CURRENT INDEX - Pushing to History", state.currentIndex);

      const newPresent = { ...getPresentState(state) }; // Copy the present state
      const newHistory = state.history.slice(0, state.currentIndex + 1); // Trim future history
      newHistory.push(newPresent); // Add new present state
      state.history = newHistory;
      state.currentIndex = newHistory.length - 1; // Set current index to latest state

      // Set hasUnsavedChanges to true whenever history changes
      if (state.currentIndex !== state.lastSavedIndex) {
        state.hasUnsavedChanges = true;
      }
    },

    /**
     * Records a pre-mutation snapshot as a restorable checkpoint.
     *
     * This is the "snapshot-BEFORE-mutate" half of the centralized `withHistory` thunk
     * (src/redux/actions/history.js). By the time this runs, the mutation has ALREADY been applied
     * to the present slot (history[currentIndex]); the payload is a fully-detached deepClone of the
     * state as it existed BEFORE that mutation. We insert it just under the mutated present and trim
     * any redo-future, so a single undo lands on the exact pre-mutation state.
     *
     * Callers must go through `withHistory` (which also enforces the isEqual dedup) rather than
     * dispatching this directly.
     */
    recordHistorySnapshot: (state, action) => {
      const before = action.payload;
      const present = state.history[state.currentIndex]; // already-mutated present
      const past = state.history.slice(0, state.currentIndex); // drop the present + any redo-future
      state.history = [...past, before, present];
      state.currentIndex = state.history.length - 1;

      if (state.currentIndex !== state.lastSavedIndex) {
        state.hasUnsavedChanges = true;
      }
    },

    undo: (state) => {
      if (state.currentIndex > 0) {
        state.isUndoRedoAction = true; // Set flag
        state.currentIndex -= 1; // Move back in history
        state.undoRedoRevision += 1; // Signal the editor canvas to re-hydrate
        console.log("CURRENT INDEX UNDO", state.currentIndex);
        state.isUndoRedoAction = false; // Reset after undo

        // Update hasUnsavedChanges state
        state.hasUnsavedChanges = state.currentIndex !== state.lastSavedIndex;
      }
    },

    redo: (state) => {
      if (state.currentIndex < state.history.length - 1) {
        state.isUndoRedoAction = true; // Set flag
        state.currentIndex += 1; // Move forward in history
        state.undoRedoRevision += 1; // Signal the editor canvas to re-hydrate
        console.log("CURRENT INDEX REDO", state.currentIndex);
        state.isUndoRedoAction = false; // Reset after redo

        // Update hasUnsavedChanges state
        state.hasUnsavedChanges = state.currentIndex !== state.lastSavedIndex;
      }
    },

    /**
     * Raises/lowers the undo/redo re-hydration suppression window. While true, `withHistory`
     * (redux/actions/history.js) applies mutations but records no history checkpoint, so the
     * reactive echoes an undo/redo triggers can't trim the redo-future (WEB-038). The undo/redo
     * handlers raise it, then lower it a short settle window later.
     */
    setHistorySuppressed: (state, action) => {
      state.historySuppressed = !!action.payload;
    },

    setType: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].type = action.payload;
    },

    setBusinessBoxWheel: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].business_box = action.payload;
    },

    setIsPrimary: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_primary = action.payload;
    },

    setIsPublic: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_public = action.payload;
    },

    setIsLocked: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_locked = action.payload;
    },

    setWheelName: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_name = action.payload;
    },

    setDescription: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].description = action.payload;
    },

    setSubTitle: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].sub_title = action.payload;
    },

    setWheelColorPalette: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].palette_id = action.payload.paletteId;
      state.history[state.currentIndex].palette_data =
        action.payload.paletteData;
    },

    setWheelSegments: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_segments = action.payload;
    },

    setAutomaticResizing: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].automatic_resizing = action.payload;
    },

    setContentHeights: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].content_heights = action.payload;
    },

    updateSegmentSuggestedColors: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, suggestedColors } = action.payload;
      if (!id) return;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, suggestedColors } : segment
      );
    },

    removeWheelSegment: (state, action) => {
      state.isUndoRedoAction = false;
      const id = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.filter((segment) => segment.id !== id);
    },

    setWheelPortrait: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_center_media = action.payload;
    },

    setCloning: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_cloning = action.payload;
    },

    setSegmentIcon: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].segment_icon = action.payload;
    },

    setIsHidden: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_hidden = action.payload;
    },

    setIsClonable: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_clonable = action.payload;
    },

    // minors-13plus B.14: creator maturity self-label. Payload is the API
    // string ("general" | "mature"), not a boolean, so redux holds exactly what
    // the wheel PUT/PATCH sends and what a GET hydrates back.
    setContentRating: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].content_rating = action.payload;
    },

    setCommentsEnabled: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].comments_enabled = action.payload;
    },

    setWheelPreview: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_preview = action.payload;
    },

    setIsDraft: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_draft = action.payload;
    },

    setIsLive: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_live = action.payload;
    },

    setVisibility: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].visibility = parseInt(action.payload);
    },

    setCategory: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].category = action.payload;
    },

    // ISP-9999 (gordon-0904-wheel-categories-multi5-plus-custom-tags)
    setCategories: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].categories = action.payload;
    },

    setWheelTags: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].wheel_tags = action.payload;
    },

    updateSegmentSetting: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, data } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id
          ? { ...segment, settings: { ...segment.settings, ...data } }
          : segment
      );
    },

    // Segment Environments (flag REACT_APP_SEGMENT_ENVIRONMENTS): merge the
    // per-segment env override(s) at the segment TOP LEVEL (env_center_media /
    // env_wallpaper_settings) — DISTINCT from the segment's own `settings`
    // (fill/wallpaper/color). null clears the override (inherit wheel default).
    updateSegmentEnvironment: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, data } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, ...data } : segment
      );
    },

    // SEGMENT CONTENT LOCK (SEGMENT-COLLAB-BUILD-SPEC.md §2, flag REACT_APP_SEGMENT_LOCK):
    // owner-side per-segment content lock. Sets the segment's TOP-LEVEL `is_locked`
    // (the BE `Activity.is_locked` field) — DISTINCT from the whole-wheel `is_locked`
    // (setIsLocked / PrivacyTab). Persists via the normal wheel Save
    // (generatePayload -> BE _update_activity) and round-trips on load
    // (wheel_segments[].is_locked). Non-paid: no lock_type / price is set here.
    setSegmentContentLock: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, is_locked } = action.payload;
      if (id === undefined || id === null) return;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, is_locked: Boolean(is_locked) } : segment
      );
    },

    // fix-editwheel-segment-paid-lock-unbuildable: the flavor (password/paid/
    // email) + sub-lock data (access code / price / emails) the owner picks
    // once Content Lock is on. Stored on the segment as `lock_type` -- BE
    // `_create_activity_locks` (yuga_wheel/api/v2/serializers/wheel.py) turns
    // this exact shape into a real LockType on save, the same nested shape
    // the Wheel-level lock already uses. `null` clears the local selection
    // (Save then omits `lock_type` entirely so an already-configured lock on
    // the server is left untouched -- see _update_activity's `"lock_type" in
    // activity_data` guard).
    setSegmentLockType: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, lock_type } = action.payload;
      if (id === undefined || id === null) return;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, lock_type } : segment
      );
    },

    updateSegmentColorPalette: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, palette, paletteId } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) => {
        return segment.id == id
          ? {
              ...segment,
              settings: {
                ...segment.settings,
                paletteId: paletteId,
                paletteData: palette,
              },
            }
          : segment;
      });
    },

    updateSegmentValue: (state, action) => {
      state.isUndoRedoAction = false;
      const { id, value } = action.payload;
      state.history[state.currentIndex].wheel_segments = state.history[
        state.currentIndex
      ].wheel_segments.map((segment) =>
        segment.id === id ? { ...segment, value } : segment
      );
    },

    setSidePaneVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSidePaneVisible = action.payload;
    },

    setToolBarVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isToolBarVisible = action.payload;
    },

    setSegmentSettingVisible: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSegmentSettingVisible =
        action.payload;
    },

    handleMenuChange: (state, action) => {
      // Top-level ephemeral UI field, NOT a history mutation — see
      // initialHistoryState.selectedMenu.
      state.selectedMenu = action.payload.num;
    },

    setDesignTabTarget: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].designTabTarget = action.payload;
    },

    clearDesignTabTarget: (state) => {
      state.history[state.currentIndex].designTabTarget = null;
    },

    // W-A0: DesignTab publishes its active sub-tab here so the wedge drag-resize
    // overlay (mounted over the wheel canvas, a different subtree) can gate its
    // grips on "user is in the Resize/Segments subsection". Top-level UI field,
    // NOT a history mutation (no isUndoRedoAction / no snapshot) — it must not be
    // part of undo/redo. DesignTab clears it (payload null) on unmount.
    setActiveDesignSubTab: (state, action) => {
      state.activeDesignSubTab = action.payload;
    },

    // V1-RESIZE: open/close the pure per-Segment resize view inside the Segment
    // Settings pane. Top-level ephemeral UI field (NOT a history mutation) — an
    // undo/redo must never flip which view the user is looking at.
    setSegmentResizeView: (state, action) => {
      state.segmentResizeView = !!action.payload;
    },

    // W-A0: the side-pane "Resize Segment" toggle writes here to enter/exit the
    // explicit resize-drag mode. Top-level UI field, NOT a history mutation (no
    // isUndoRedoAction / no snapshot) — it must not be part of undo/redo. Both
    // side-pane toggles and the builder's unmount cleanup dispatch this.
    setResizeSegmentMode: (state, action) => {
      state.resizeSegmentMode = !!action.payload;
    },

    handleCloseMenu: (state) => {
      // Top-level ephemeral UI field, NOT a history mutation — see
      // initialHistoryState.selectedMenu.
      state.selectedMenu = null;
    },

    handleFroalaFocus: (state, action) => {
      state.froala_focus = action.payload;
    },

    setWheelRotateBoxVisibility: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].is_rotate_box_open = action.payload;
    },

    setTakeScreenshot: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].take_screenshot = action.payload;
    },

    // fe-builder-first-edit-after-load-silently-dropped: the Details pane
    // (wheel title/subtitle/description) mounts and is interactive the
    // moment the builder page mounts, independent of the wheel-fetch that
    // feeds this action -- so `currentIndex` can already have advanced past
    // 0 (a real edit made while the fetch was in flight) by the time this
    // runs. Merging the server payload straight into `history[currentIndex]`
    // (as before) clobbers whatever fields that edit touched with the
    // server's stale-at-edit-time values, and the hydration success handler's
    // very next `markChangesSaved()` then marks the corrupted result "saved"
    // -- the edit is gone with no error and no autosave ever fired.
    //
    // Every builder mutation goes through `withHistory` (redux/actions/
    // history.js), which clones the PRE-mutation present as `before` and
    // applies the mutation to the present slot in place -- so a pre-hydration
    // present is always `{...history[0], <the touched fields overwritten>}`.
    // That makes the touched fields diffable against history[0]: rebase the
    // server payload as the new baseline, then replay just those fields back
    // on top so the edit survives instead of being silently overwritten.
    addWheelData: (state, action) => {
      state.isUndoRedoAction = false;

      // ISP-9999 (stgfix-1007-P0-collaborator-edit-wheel-crash-reporter-shape):
      // a legacy wheel row can carry a non-array `categories` (measured live:
      // `{}` on a pre-multiselect wheel) -- DetailsTab's
      // `categories.includes(...)` throws on anything but an array, crashing
      // the whole Builder (every tab mounts on load, Info/"0" is the default)
      // the instant such a wheel is opened, owner or collaborator alike.
      // Normalize at this single hydration chokepoint so every consumer
      // (DetailsTab render, the categories/tags autosave signature,
      // generateWheelPayload) only ever sees a real array, the same way
      // wheel_center_media/show_segment_icon are already normalized at this
      // action's call site.
      const payload = Array.isArray(action.payload.categories)
        ? action.payload
        : { ...action.payload, categories: [] };

      if (state.currentIndex === 0) {
        state.history[0] = { ...state.history[0], ...payload };
        state.preHydrationEditPending = false;
        return;
      }

      const preHydrationBaseline = state.history[0];
      const editedPresent = state.history[state.currentIndex];
      const editedFields = {};
      for (const key of Object.keys(editedPresent)) {
        if (!isEqual(editedPresent[key], preHydrationBaseline[key])) {
          editedFields[key] = editedPresent[key];
        }
      }

      state.history[0] = { ...preHydrationBaseline, ...payload };
      state.history[state.currentIndex] = {
        ...state.history[0],
        ...editedFields,
      };
      // fe-builder-first-edit-after-load-silently-dropped: a non-empty
      // editedFields means a REAL edit was made before this fetch resolved and
      // was just reconciled on top of the server payload above -- it has been
      // kept in redux but never sent in a PUT. The hydrate success handler
      // must not immediately re-anchor `lastSavedIndex` onto this index (its
      // own unconditional markChangesSaved(), WEB-E2E-3) or the edit reads
      // "All changes saved" while actually unpersisted forever.
      state.preHydrationEditPending = Object.keys(editedFields).length > 0;
    },

    // T28.5 (audit-10151500-T285-time-machine-limits, measured live web-stg
    // wheel 508549): AUTHORITATIVE re-hydration after a server-side Version
    // History restore. addWheelData above replays every present field that
    // differs from history[0] as an "edit made before the fetch resolved" --
    // right for the first load, wrong after a restore: every edit made earlier
    // in this session (an added Segment, the title editor's "<p>" re-wrap of
    // wheel_name) differs from the first-load baseline, so it was replayed over
    // the restored wheel and the next autosave PUT it back, silently undoing
    // the restore. Here the server payload wins outright: the restored wheel
    // becomes the only (already saved) history entry, and undoRedoRevision is
    // bumped so the open Segment editor / Style tab re-derive from it.
    replaceWheelDataFromServer: (state, action) => {
      state.isUndoRedoAction = false;
      const payload = Array.isArray(action.payload.categories)
        ? action.payload
        : { ...action.payload, categories: [] };
      state.history = [{ ...state.history[state.currentIndex], ...payload }];
      state.currentIndex = 0;
      state.lastSavedIndex = 0;
      state.hasUnsavedChanges = false;
      state.preHydrationEditPending = false;
      state.undoRedoRevision += 1;
    },

    setLockEmails: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].lock_type.permission_lock.emails =
        action.payload;
    },

    setLockEmailPermission: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].lock_type.permission_lock.is_active = action.payload;
    },

    setLockAccessCodes: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[
        state.currentIndex
      ].lock_type.access_code_lock = action.payload;
    },

    setIsSavingWheel: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isSavingWheel = action.payload;
    },

    setWheelId: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].id = action.payload;
    },

    resetBuilder: (state) => {
      state.history = [initialState];
      state.currentIndex = 0;
      state.undoRedoRevision = 0;
      state.historySuppressed = false;
      // Reset the saved-baseline + dirty flag too. Without this they leaked from
      // the previous builder session: currentIndex went back to 0 but a stale
      // lastSavedIndex (>0) / hasUnsavedChanges (true) survived, so the NEXT
      // /builder/<id> open showed a false "Unsaved changes" chip at t≈0 with no
      // user edit (residual of QA row 23/19). A freshly-reset builder is, by
      // definition, at its loaded baseline (index 0) with nothing unsaved.
      state.lastSavedIndex = 0;
      state.hasUnsavedChanges = false;
    },

    setGlobalCornerRounding: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.cornerRounding = action.payload;
      // Get the new corner rounding value
      const newCornerRounding = action?.payload;

      if (newCornerRounding !== undefined) {
        // Loop through each segment to update container nodes
        present.wheel_segments = updateWheelSegmentsNodes(
          present.wheel_segments,
          (node) => {
            if (node.type?.resolvedName === "ContainerWidget" && node.props) {
              node.props.radius = newCornerRounding;
            } else if (node?.props?.borderRadius) {
              node.props.borderRadius = newCornerRounding;
            }
          }
        );
      }
      present.isGlobalCornerRoundingApplied = true;
    },

    setGlobalMargin: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.margin, action.payload);

      const newMargin = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.margin) {
            node.props.margin = newMargin;
          }
        }
      );

      present.isGlobalMarginApplied = true;
    },

    setGlobalborderThickness: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.borderThickness = action.payload;

      const newBorderThickness = action?.payload;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node.type?.resolvedName === "ContainerWidget" && node.props) {
            node.props.border.width = newBorderThickness;
          } else if (node?.props?.borderWidth) {
            node.props.borderWidth = newBorderThickness;
          }
        }
      );
      present.isGlobalBorderThicknessApplied = true;
    },

    setGlobalHeadingProps: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.headingProps, action.payload);

      const newHeadingProps = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.headingProps) {
            node.props.headingProps = newHeadingProps;
          }
        }
      );

      present.isGlobalHeadingPropsApplied = true;
    },
    setGlobalParagraphProps: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      merge(present.globalSettings.paragraphProps, action.payload);

      const newParagraphProps = action?.payload;

      // Update nodes in wheel segments
      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.paragraphProps) {
            node.props.paragraphProps = newParagraphProps;
          }
        }
      );

      present.isGlobalParagraphPropsApplied = true;
    },

    setGlobalLineHeight: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);
      present.globalSettings.lineHeight = action.payload;

      const newLineHeight = action?.payload;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node?.props?.lineHeight) {
            node.props.lineHeight = newLineHeight;
          }
        }
      );
      present.isGlobalLineHeightApplied = true;
    },
    setGlobalSettings: (state, action) => {
      state.isUndoRedoAction = false;
      const present = getPresentState(state);

      // Deep-merge so nested objects (like headingProps, paragraphProps, margin) get updated
      // without destroying other keys.
      merge(present.globalSettings, action.payload);

      const newBorderWidth = action?.payload?.borderThickness;
      const newMargin = action?.payload?.margin;
      const newCornerRounding = action?.payload?.cornerRounding;

      present.wheel_segments = updateWheelSegmentsNodes(
        present.wheel_segments,
        (node) => {
          if (node.type?.resolvedName === "ContainerWidget" && node.props) {
            node.props.border.width = newBorderWidth;
            node.props.radius = newCornerRounding;
            node.props.margin = newMargin;
          } else if (
            (node.type?.resolvedName === "TextWidget" ||
              node.type?.resolvedName === "ImageWithText") &&
            node.props
          ) {
            node.props.lineHeight = action?.payload?.lineHeight;
            node.props.headingProps = action?.payload?.headingProps;
            node.props.paragraphProps = action?.payload?.paragraphProps;
          } else if (
            (node.type?.resolvedName === "DividerWidget" ||
              node.type?.resolvedName === "Divider") &&
            node.props
          ) {
            // For dividers, we only want to apply the margin, NOT thickness
            node.props.margin = newMargin;
          } else if (
            (node.type?.resolvedName === "Image" ||
              node.type?.resolvedName === "ImageWidget") &&
            node.props
          ) {
            // For Image blocks, apply border radius, border width, and margin
            node.props.margin = newMargin;
            node.props.borderRadius = newCornerRounding;
            node.props.borderWidth = newBorderWidth;
            console.log("Applying global settings to Image:", node.props);
          } else {
            // For other components
            if (node.props?.borderRadius !== undefined) {
              node.props.borderRadius = newCornerRounding;
            }
            if (node.props?.borderWidth !== undefined) {
              node.props.borderWidth = newBorderWidth;
            }
            if (node.props?.margin !== undefined) {
              node.props.margin = newMargin;
            }
          }
        }
      );

      present.isGlobalSettingsApplied = true;
    },
    setisGlobalSettingsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalSettingsApplied =
        action.payload;
    },
    setisisGlobalBorderThicknessApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalBorderThicknessApplied =
        action.payload;
    },
    setisGlobalMarginApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalMarginApplied = action.payload;
    },
    setisGlobalCornerRoundingApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalCornerRoundingApplied =
        action.payload;
    },
    setisGlobalHeadingPropsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalHeadingPropsApplied =
        action.payload;
    },
    setisGlobalParagraphPropsApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalParagraphPropsApplied =
        action.payload;
    },
    setisGlobalLineHeightApplied: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].isGlobalLineHeightApplied =
        action.payload;
    },
    // Add this new reducer to mark changes as saved
    //
    // daniyal-icon-persist (3rd cause, save-baseline race): a caller mid-PUT
    // must be able to mark the index its payload was actually BUILT from as
    // saved, not whatever `currentIndex` has advanced to by the time the PUT
    // resolves (e.g. a segment icon picked while the PUT was in flight). The
    // optional `action.payload` carries that captured index; every existing
    // bare `markChangesSaved()` caller keeps falling back to `currentIndex`.
    markChangesSaved: (state, action) => {
      state.lastSavedIndex = action.payload ?? state.currentIndex;
      // Derive, don't force-clear: if `currentIndex` has moved past the index
      // that was actually saved (the race above), the wheel must STILL read
      // dirty so a follow-up autosave persists what's live now. Same idiom as
      // the other history reducers in this file (see line ~232/245).
      state.hasUnsavedChanges = state.currentIndex !== state.lastSavedIndex;
    },
    // Add action to update the character count for the selected segment
    setSelectedSegmentCharCount: (state, action) => {
      state.isUndoRedoAction = false;
      state.history[state.currentIndex].selected_segment_char_count =
        action.payload;
    },
    /**
     * LIVE CO-EDIT (BUG-collaboration-a28f0f): apply an INBOUND REMOTE op from a
     * peer to the shared editor state so a second collaborator's builder actually
     * reflects the change. Payload is the bounded op forwarded by
     * `useWheelCoedit`'s `onRemoteOp` hook point: `{ scope, patch }`.
     *   - `scope` == null / undefined -> a WHEEL-LEVEL edit: shallow-merge `patch`
     *     into the present wheel fields (e.g. { wheel_name }, { sub_title }).
     *   - `scope` set -> a SEGMENT-scoped edit: match the segment by id (string- or
     *     number-equal) and shallow-merge `patch` into that segment (same shape as
     *     the local `updateSegmentEnvironment` reducer).
     * Last-write-wins ordering is upstream: the hook only forwards peer ops
     * (`!message.own`, so a local echo is never double-applied) and the coedit
     * reducer tracks `version`/`conflict` for the surfaced banner. A malformed or
     * empty op is a NO-OP (never throws — a bad frame must not corrupt the editor).
     * Marks `hasUnsavedChanges` so the autosave chip reflects the incoming change;
     * does NOT push an undo checkpoint (a remote edit is not the local user's undo
     * step).
     */
    applyRemoteOp: (state, action) => {
      const op = action.payload;
      if (!op || typeof op !== "object") return;
      const patch = op.patch;
      if (!patch || typeof patch !== "object" || Array.isArray(patch)) return;
      const present = state.history[state.currentIndex];
      if (!present) return;
      state.isUndoRedoAction = false;

      if (op.scope == null) {
        // Wheel-level edit: shallow-merge onto the present wheel fields.
        Object.assign(present, patch);
        state.hasUnsavedChanges = true;
        state.remoteOpSeq = (state.remoteOpSeq || 0) + 1;
        return;
      }

      // Segment-scoped edit: find the target segment by id and merge the patch.
      const segments = present.wheel_segments || [];
      let applied = false;
      present.wheel_segments = segments.map((segment) => {
        if (segment && String(segment.id) === String(op.scope)) {
          applied = true;
          return { ...segment, ...patch };
        }
        return segment;
      });
      if (applied) {
        state.hasUnsavedChanges = true;
        state.remoteOpSeq = (state.remoteOpSeq || 0) + 1;
      }
    },

    addNewSegmentToWheel(state, action) {
      const current = state.history[state.currentIndex];
      const { updatedSegments, newSegment } = action.payload;

      // If updatedSegments are provided, use them; otherwise just mark existing segments as unselected
      const updatedWheelData =
        updatedSegments ||
        current.wheel_segments.map((segment) => ({
          ...segment,
          selected: false,
        }));

      // Generate a unique ID if not provided
      const segmentId =
        newSegment.id ||
        Math.max(...updatedWheelData.map((s) => s.id || 0), 0) + 1;

      // Calculate position for the new segment
      const lastPosition =
        updatedWheelData.length > 0
          ? Math.max(...updatedWheelData.map((s) => s.position || 0))
          : 0;
      const newPosition = lastPosition + 10;

      // Parse nodes if they're in string format
      const nodes =
        typeof newSegment.nodes === "string"
          ? newSegment.nodes
          : JSON.stringify(newSegment.nodes);

      // Create the final new segment with proper attributes
      const finalNewSegment = {
        ...newSegment,
        id: segmentId,
        selected: true,
        position: newSegment.position || newPosition,
        is_modified: true,
        nodes: nodes,
        // Ensure settings are properly set
        settings: {
          ...(newSegment.settings || {}),
          color: newSegment.settings?.color,
        },
      };

      // Update wheel segments with both updated existing segments and new segment
      current.wheel_segments = [...updatedWheelData, finalNewSegment];

      // Mark that we have unsaved changes
      state.hasUnsavedChanges = true;
    },

    // T22.125: append segments a teammate added and saved (found by
    // pages/SegmentBuilder/peerAddedSegments.js on their coedit_saved). Like
    // applyRemoteOp this bumps remoteOpSeq, so the builder re-anchors its dirty
    // baseline instead of echo-saving them.
    // review-1008-Frontend-6929: the segment goes into EVERY history entry,
    // not just the present one. It is the teammate's work, not a local undo
    // step: with only the present patched, an Undo of the user's own earlier
    // edit landed on a snapshot without it, and the next save deleted it from
    // the server (measured on web-stg main.7920884e.js, API 5 -> 4).
    appendRemoteSegments: (state, action) => {
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      const present = state.history[state.currentIndex];
      if (!present || incoming.length === 0) return;
      const presentIds = new Set(
        (present.wheel_segments || []).map((s) => s && String(s.id)),
      );
      const fresh = incoming.filter((s) => s && !presentIds.has(String(s.id)));
      if (fresh.length === 0) return;
      state.isUndoRedoAction = false;
      state.history.forEach((entry) => {
        if (!entry) return;
        const segments = entry.wheel_segments || [];
        const have = new Set(segments.map((s) => s && String(s.id)));
        const missing = fresh.filter((s) => !have.has(String(s.id)));
        if (missing.length === 0) return;
        entry.wheel_segments = [...segments, ...missing].sort(
          (a, b) => (a?.position ?? 0) - (b?.position ?? 0),
        );
      });
      state.hasUnsavedChanges = true;
      state.remoteOpSeq = (state.remoteOpSeq || 0) + 1;
    },

    // T22.172: drop segments a teammate removed and saved (found by
    // pages/SegmentBuilder/peerAddedSegments.js on their coedit_saved). Every
    // history entry loses them, so neither this snapshot nor an Undo to an
    // older one can carry them into the next full-list save, which would
    // re-create them server-side. Bumps remoteOpSeq like appendRemoteSegments.
    removeRemoteSegments: (state, action) => {
      const ids = new Set(
        (Array.isArray(action.payload) ? action.payload : []).map(String),
      );
      const present = state.history[state.currentIndex];
      if (!present || ids.size === 0) return;
      const hit = (present.wheel_segments || []).some(
        (s) => s && ids.has(String(s.id)),
      );
      if (!hit) return;
      state.history.forEach((entry) => {
        if (entry && Array.isArray(entry.wheel_segments)) {
          entry.wheel_segments = entry.wheel_segments.filter(
            (s) => !(s && ids.has(String(s.id))),
          );
        }
      });
      state.isUndoRedoAction = false;
      state.hasUnsavedChanges = true;
      state.remoteOpSeq = (state.remoteOpSeq || 0) + 1;
    },
  },
});

export const {
  undo,
  redo,
  recordHistorySnapshot,
  setHistorySuppressed,
  setType,
  setBusinessBoxWheel,
  setIsPrimary,
  setIsPublic,
  setIsLocked,
  setWheelName,
  setDescription,
  setSubTitle,
  setWheelSegments,
  setAutomaticResizing,
  setContentHeights,
  removeWheelSegment,
  updateSegmentSuggestedColors,
  setWheelPortrait,
  setCloning,
  setSegmentIcon,
  setIsHidden,
  setVisibility,
  setCategory,
  setCategories,
  setWheelTags,
  updateSegmentSetting,
  updateSegmentEnvironment,
  setSegmentContentLock,
  setSegmentLockType,
  updateSegmentValue,
  setSidePaneVisible,
  setToolBarVisible,
  setSegmentSettingVisible,
  handleMenuChange,
  setDesignTabTarget,
  clearDesignTabTarget,
  setActiveDesignSubTab,
  setSegmentResizeView,
  setResizeSegmentMode,
  setWheelPreview,
  handleCloseMenu,
  setWheelRotateBoxVisibility,
  addWheelData,
  replaceWheelDataFromServer,
  setIsSavingWheel,
  resetBuilder,
  pushToHistoryAction,
  handleFroalaFocus,
  setLockEmails,
  setLockAccessCodes,
  setIsClonable,
  setContentRating,
  setCommentsEnabled,
  setIsLive,
  setGlobalSettings,
  setIsDraft,
  setWheelId,
  updateSegmentColorPalette,
  setisGlobalSettingsApplied,
  setGlobalCornerRounding,
  setGlobalMargin,
  setGlobalborderThickness,
  setisGlobalCornerRoundingApplied,
  setisGlobalMarginApplied,
  setisisGlobalBorderThicknessApplied,
  setisGlobalHeadingPropsApplied,
  setisGlobalParagraphPropsApplied,
  setisGlobalLineHeightApplied,
  setGlobalHeadingProps,
  setGlobalParagraphProps,
  setGlobalLineHeight,
  setWheelColorPalette,
  markChangesSaved,
  setTakeScreenshot,
  setSelectedSegmentCharCount,
  addNewSegmentToWheel,
  setLockEmailPermission,
  applyRemoteOp,
  appendRemoteSegments,
  removeRemoteSegments,
} = segmentBuilder.actions;

export default segmentBuilder.reducer;
